import React, { useMemo } from "react";
import { useLocale } from "lib/hooks/useLocale";
import { EmiDetailsType, PaymentMode, PaymentModeType } from "lib/types/payments";
import { useIsSubscribeMode } from "lib/hooks/useIsSubscribeMode";
import { parseMethodUiAttributes, paymentMethodsConfig, getAdditionalChargeText } from "lib/utils/payments";
import { classNames } from "lib/utils/helpers";
import { currencyFormatter } from "lib/utils/formatters";
import { CaretRight } from "@phosphor-icons/react";
import InvalidOffersDialog from "components/payments/dialogs/InvalidOffers";
import { useMethod } from "components/payments/methods/useMethod";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import SuggestedMethodWrapper from "components/common/SuggestedMethodWrapper";
import OfferBadge from "components/common/OfferBadge";
import LogosStack, { MethodLogo } from "components/common/LogosStack";

interface MethodCardProps {
  modeData: PaymentMode;
  inFocus?: boolean;
}

const MethodCard: React.FC<MethodCardProps> = ({ modeData, inFocus }) => {
  const { t } = useLocale();
  const {
    name,
    amount: total,
    metadata,
    isAvailable,
    unavailabilityReasons,
    emiPlans,
    additionalCharges,
    codCharges,
  } = modeData;
  const { logo: customLogoUrl, additionalChargeType = "FLAT", additionalChargeValue = 0 } = metadata || {};
  const {
    state: { merchant, checkoutMetadata },
  } = useMerchantContext();
  const {
    hasDiscountOrFreebie,
    handleMethodClick,
    invalidOfferDialogOpen,
    handleInvalidOfferDialogClose,
    billing,
    offerText,
  } = useMethod({ modeData, inFocus });

  const isSubscribeMode = useIsSubscribeMode();

  const methodUiAttributes = useMemo(() => {
    return parseMethodUiAttributes({ mode: modeData, checkoutMetadata, isSubscribeMode });
  }, [modeData, checkoutMetadata, isSubscribeMode]);

  const shouldShowAdditionalCharge = !["SPLIT_COD", "COD"].includes(name) && additionalChargeValue > 0;
  const isSplitCODMode = name === "SPLIT_COD";
  const isCODBasedMode = ["SPLIT_COD", "COD"].includes(name);
  const excludeCodCharges = modeData?.metadata.excludeCodCharges ?? false;
  const shouldShowCodCharge =
    isCODBasedMode && Boolean(codCharges && codCharges > 0) && !(isSplitCODMode && excludeCodCharges);

  const emiDetails: EmiDetailsType[] =
    emiPlans?.map((e) => ({
      tenure: e?.tenure,
      remainingAmount: e?.remainingAmount,
      downPayment: e?.downPayment,
    })) ?? [];

  const additionalChargeText = useMemo(() => {
    return getAdditionalChargeText({
      mode: name,
      additionalChargeType: additionalChargeType,
      additionalChargeValue: additionalChargeValue,
      rootLevelAdditionalChargeValue: additionalCharges,
      billing,
      shouldShowAdditionalCharge,
      shouldShowCodCharge,
      codCharges,
    });
  }, [
    name,
    additionalChargeType,
    additionalChargeValue,
    billing,
    shouldShowAdditionalCharge,
    shouldShowCodCharge,
  ]);

  const renderUnavailableReason = () => {
    if (!unavailabilityReasons?.length) return <></>;

    const hasShippingBlocker = unavailabilityReasons.some((e) => e.type === "SHIPPING_HANDLES");
    if (hasShippingBlocker) {
      return (
        <p
          className={`flex items-center gap-1 space-x-1 text-sm font-medium `}
          style={{ color: methodUiAttributes.paymentCard.textColor }}>
          <span>{t("select_shipping")}</span> <CaretRight size={14} weight="bold" />
        </p>
      );
    }
    return (
      <p
        className={`flex items-center gap-1 space-x-1 text-sm font-medium `}
        style={{ color: methodUiAttributes.paymentCard.textColor }}>
        <span>{t("remove_offers")}</span>{" "}
        <CaretRight size={14} color={methodUiAttributes.paymentCard.textColor} weight="bold" />
      </p>
    );
  };

  const renderSnapmintEMI = () => {
    const monthsValue = emiDetails?.map((e) => e?.tenure).join("/");
    return (
      <div className={`flex items-center space-x-2 ${hasDiscountOrFreebie ? "pt-3" : ""}`}>
        <div className={`flex flex-col items-end justify-end space-y-1 `}>
          <span style={{ color: methodUiAttributes.paymentCard.textColor }} className="text-sm font-medium">
            {currencyFormatter(emiDetails[0]?.downPayment)} {Boolean(emiDetails?.length) ? t("now") : ""}
          </span>
          {Boolean(emiDetails?.length) && (
            <span
              className="text-xs font-normal "
              style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              <span
                className="text-xs font-normal "
                style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
                {"Rest "}
                {t("in_x_months", { months: monthsValue })}
              </span>
            </span>
          )}
          {shouldShowAdditionalCharge && (
            <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {additionalChargeText}
            </div>
          )}

          {shouldShowCodCharge && (
            <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {additionalChargeText}
            </div>
          )}
        </div>
        <CaretRight
          size={14}
          className="h-4 w-4 "
          color={methodUiAttributes.paymentCard.textColor}
          weight="bold"
        />
      </div>
    );
  };

  const renderSimplPayInThree = () => {
    const monthsValue = emiDetails?.map((e) => e?.tenure).join("/");
    return (
      <div className={`flex items-center space-x-2 `}>
        <div className={`flex flex-col items-end justify-end space-y-1 `}>
          <span style={{ color: methodUiAttributes.paymentCard.textColor }} className="text-sm font-medium">
            {currencyFormatter(emiDetails[0]?.downPayment)} {Boolean(emiDetails?.length) ? t("now") : ""}
          </span>
          {Boolean(emiDetails?.length) && (
            <span
              className="text-xs font-normal "
              style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {currencyFormatter(emiDetails[0]?.remainingAmount)}
              <span
                className="text-xs font-normal "
                style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
                {" "}
                {t("in_x_months", { months: monthsValue })}
              </span>
            </span>
          )}
          {shouldShowAdditionalCharge && (
            <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {additionalChargeText}
            </div>
          )}

          {shouldShowCodCharge && (
            <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {additionalChargeText}
            </div>
          )}
        </div>
        <CaretRight className="h-4 w-4 " color={methodUiAttributes.paymentCard.textColor} weight="bold" />
      </div>
    );
  };

  const renderVelocity = () => {
    const monthsValue = emiDetails?.map((e) => e?.tenure).join("/");
    return (
      <div className={`flex items-center space-x-2 ${hasDiscountOrFreebie ? "pt-3" : ""}`}>
        <div className={`flex flex-col items-end justify-end space-y-1 `}>
          <span style={{ color: methodUiAttributes.paymentCard.textColor }} className="text-sm font-medium">
            {currencyFormatter(emiDetails[0]?.downPayment)} {Boolean(emiDetails?.length) ? t("now") : ""}
          </span>
          {Boolean(emiDetails?.length) && (
            <span
              className="text-xs font-normal "
              style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {"Rest "}
              {t("in_x_months", { months: monthsValue })}
            </span>
          )}
          {shouldShowAdditionalCharge && (
            <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {additionalChargeText}
            </div>
          )}

          {shouldShowCodCharge && (
            <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
              {additionalChargeText}
            </div>
          )}
        </div>
        <CaretRight
          size={14}
          className="h-4 w-4 "
          color={methodUiAttributes.paymentCard.textColor}
          weight="bold"
        />
      </div>
    );
  };

  const renderSplitCODTagTile = () => {
    return (
      <div className={`flex items-center justify-end `}>
        <div className={`flex flex-col items-end justify-end space-y-1`}>
          <div
            className={classNames("text-gray-dark", " py-0.5 text-xs font-medium ")}
            style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
            + {currencyFormatter(metadata.codAmount)} COD
          </div>
        </div>
      </div>
    );
  };

  const getPaymentAmountDisplay = () => {
    if (name === "SNPM" && Boolean(emiDetails?.length)) {
      return renderSnapmintEMI();
    }

    if (name === "VELOCITY" && Boolean(emiDetails?.length)) {
      return renderVelocity();
    }

    if (name === "SIMPL" && Boolean(emiDetails?.length)) {
      return renderSimplPayInThree();
    }

    if (total) {
      return (
        <div className="flex items-center gap-1">
          <div className="flex flex-col gap-0.5">
            <span
              className="flex text-sm font-medium items-end ml-auto"
              style={{ color: methodUiAttributes.paymentCard.textColor }}>
              {currencyFormatter(total)}
              {isSplitCODMode && <span className="ml-1">Now</span>}
            </span>
            {isSplitCODMode && renderSplitCODTagTile()}
            {shouldShowAdditionalCharge && (
              <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
                {additionalChargeText}
              </div>
            )}

            {shouldShowCodCharge && (
              <div className="text-xs" style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
                {additionalChargeText}
              </div>
            )}
          </div>

          <CaretRight size={14} color={methodUiAttributes.paymentCard.textColor} weight="bold" />
        </div>
      );
    }

    return null;
  };

  const renderPaymentMethod = () => {
    return (
      <div
        className={classNames(
          "group flex items-center px-3.5 py-2.5 border gap-3 cursor-pointer w-full min-h-14 transition-colors",
          isAvailable
            ? "hover:shadow-[0_0_0_100px_rgba(113,113,123,0.15)_inset]"
            : " shadow-[0_0_0_100px_rgba(255,255,255,0.5)_inset] hover:shadow-[0_0_0_100px_rgba(255,255,255,0.6)_inset]",
        )}
        onClick={handleMethodClick}
        style={{
          borderColor: isAvailable ? methodUiAttributes.paymentCard.borderColor : "transparent",
          backgroundColor: methodUiAttributes.paymentCard.backgroundColor,
          borderRadius: `${methodUiAttributes.paymentCard.borderRadius}px`,
        }}>
        {methodUiAttributes.layout === "ICONS" && (
          <div className={classNames(!isAvailable ? "opacity-40 " : "")}>
            {Boolean(customLogoUrl) ? (
              <img
                src={customLogoUrl}
                alt={paymentMethodsConfig[name]?.header}
                className="h-9 w-9 object-contain"
              />
            ) : (
              <MethodLogo color={methodUiAttributes.paymentCard.iconColor} mode={name} />
            )}
          </div>
        )}

        <div
          className={classNames(
            "flex w-full flex-1 flex-col items-start justify-start space-y-1",
            !isAvailable ? "opacity-40" : "",
          )}>
          <div className="flex w-full flex-row items-center justify-start space-x-3">
            <div className="flex flex-1 flex-col justify-center gap-1">
              <div className="flex gap-1 items-center">
                <h3
                  className="text-sm font-medium max-w-44"
                  style={{
                    color: isAvailable ? methodUiAttributes.paymentCard.textColor : "text-gray-dark",
                  }}>
                  {methodUiAttributes.title ? methodUiAttributes.title : paymentMethodsConfig[name]?.header}
                </h3>
                {methodUiAttributes.layout === "LOGOS" && <LogosStack logos={methodUiAttributes.icons} />}
              </div>

              {!methodUiAttributes.paymentCard.isSubtitleHidden && (
                <p
                  className="overflow-hidden text-xs font-normal max-w-48"
                  style={{ color: methodUiAttributes.paymentCard.subtitleColor }}>
                  {methodUiAttributes.subtitle
                    ? methodUiAttributes.subtitle
                    : paymentMethodsConfig[name]?.subheader}
                </p>
              )}
              {hasDiscountOrFreebie && (
                <OfferBadge
                  text={offerText || ""}
                  backgroundColor={methodUiAttributes.offerBadge.backgroundColor}
                  textColor={methodUiAttributes.offerBadge.textColor}
                />
              )}
            </div>
          </div>
        </div>

        <div className="ml-auto flex flex-col items-end gap-1">
          {!isAvailable && renderUnavailableReason()}
          {isAvailable && <div className="flex items-center gap-1">{getPaymentAmountDisplay()}</div>}
        </div>
      </div>
    );
  };

  return (
    <>
      <SuggestedMethodWrapper
        isSuggested={methodUiAttributes.isSuggested}
        title={methodUiAttributes.suggestedTile?.title}
        backgroundColor={methodUiAttributes.suggestedTile?.backgroundColor}
        textColor={methodUiAttributes.suggestedTile?.textColor}>
        {renderPaymentMethod()}
      </SuggestedMethodWrapper>

      <InvalidOffersDialog
        isOpen={invalidOfferDialogOpen}
        handleClose={handleInvalidOfferDialogClose}
        unavailabilityReasons={unavailabilityReasons}
        mode={modeData}
      />
    </>
  );
};

export default React.memo(MethodCard);
